import { Routes } from '@angular/router';
import { authGuard, noAuthGuard } from './guards/auth.guard';

export const routes: Routes = [
  // Authentication routes (no layout) - Lazy loaded
  // These routes are only accessible when NOT logged in
  {
    path: 'login',
    loadComponent: () => import('./components/auth/login/login.component')
      .then(m => m.LoginComponent),
    canActivate: [noAuthGuard],
    data: { title: 'Login' }
  },
  {
    path: 'register',
    loadComponent: () => import('./components/auth/register/register.component')
      .then(m => m.RegisterComponent),
    canActivate: [noAuthGuard],
    data: { title: 'Register' }
  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./components/auth/forgot-password/forgot-password.component')
      .then(m => m.ForgotPasswordComponent),
    canActivate: [noAuthGuard],
    data: { title: 'Forgot Password' }
  },
  {
    path: 'verify',
    loadComponent: () => import('./components/auth/two-step-verification/two-step-verification.component')
      .then(m => m.TwoStepVerificationComponent),
    data: { title: 'Two-Step Verification' }
  },

  // Main application routes (with layout) - All lazy loaded
  // All these routes require authentication
  {
    path: '',
    loadComponent: () => import('./components/layout/main-layout/main-layout.component')
      .then(m => m.MainLayoutComponent),
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: '/dashboard', pathMatch: 'full' },
      {
        path: 'dashboard',
        loadComponent: () => import('./components/dashboard/dashboard.component')
          .then(m => m.DashboardComponent),
        data: { title: 'Dashboard' }
      },

      // Ordering & Procurement routes
      {
        path: 'catalog',
        loadComponent: () => import('./components/ordering/catalog/catalog')
          .then(m => m.Catalog),
        data: { title: 'Product Catalog' }
      },
      {
        path: 'orders',
        loadComponent: () => import('./components/ordering/orders/orders')
          .then(m => m.Orders),
        data: { title: 'Orders' }
      },
      {
        path: 'order-guides',
        loadComponent: () => import('./components/ordering/order-guides/order-guides')
          .then(m => m.OrderGuides),
        data: { title: 'Order Guides' },
        children: [
          { path: '', redirectTo: 'list', pathMatch: 'full' },
          {
            path: 'list',
            loadComponent: () => import('./components/ordering/order-guides/order-guide-list/order-guide-list.component')
              .then(m => m.OrderGuideListComponent),
            data: { title: 'Order Guides' }
          },
          {
            path: 'wizard',
            loadComponent: () => import('./components/ordering/order-guides/order-guide-wizard/order-guide-wizard.component')
              .then(m => m.OrderGuideWizardComponent),
            data: { title: 'Create Order Guide' }
          },
          {
            path: 'edit/:id',
            loadComponent: () => import('./components/ordering/order-guides/order-guide-editor/order-guide-editor.component')
              .then(m => m.OrderGuideEditorComponent),
            data: { title: 'Edit Order Guide' }
          },
          {
            path: 'quick-order/:id',
            loadComponent: () => import('./components/ordering/order-guides/quick-order/quick-order.component')
              .then(m => m.QuickOrderComponent),
            data: { title: 'Quick Order' }
          },
          {
            path: 'details/:id',
            loadComponent: () => import('./components/ordering/order-guides/order-guide-details/order-guide-details.component')
              .then(m => m.OrderGuideDetailsComponent),
            data: { title: 'Order Guide Details' }
          }
        ]
      },
      {
        path: 'order-guides/templates',
        loadComponent: () => import('./components/ordering/templates/template-library.component')
          .then(m => m.TemplateLibraryComponent),
        data: { title: 'Order Guide Templates' }
      },
      {
        path: 'order-guides/templates/customize/:id',
        loadComponent: () => import('./components/ordering/templates/template-customize.component')
          .then(m => m.TemplateCustomizeComponent),
        data: { title: 'Customize Template' }
      },
      {
        path: 'suppliers',
        loadComponent: () => import('./components/ordering/suppliers/suppliers')
          .then(m => m.Suppliers),
        data: { title: 'Suppliers' }
      },
      {
        path: 'suppliers/comparison',
        loadComponent: () => import('./components/ordering/suppliers/supplier-comparison/supplier-comparison')
          .then(m => m.SupplierComparisonComponent),
        data: { title: 'Supplier Comparison' }
      },
      {
        path: 'suppliers/:id',
        loadComponent: () => import('./components/ordering/suppliers/supplier-profile/supplier-profile')
          .then(m => m.SupplierProfile),
        data: { title: 'Supplier Profile' }
      },

      // Checkout routes
      {
        path: 'checkout',
        loadComponent: () => import('./components/ordering/checkout/checkout-page/checkout-page.component')
          .then(m => m.CheckoutPageComponent),
        data: { title: 'Checkout' },
        children: [
          { path: '', redirectTo: 'summary', pathMatch: 'full' },
          {
            path: 'summary',
            loadComponent: () => import('./components/ordering/checkout/cart-summary/cart-summary.component')
              .then(m => m.CartSummaryComponent),
            data: { title: 'Cart Summary' }
          },
          {
            path: 'payment',
            loadComponent: () => import('./components/ordering/checkout/payment-selection/payment-selection.component')
              .then(m => m.PaymentSelectionComponent),
            data: { title: 'Payment Selection' }
          },
          {
            path: 'review',
            loadComponent: () => import('./components/ordering/checkout/order-review/order-review.component')
              .then(m => m.OrderReviewComponent),
            data: { title: 'Review Order' }
          },
          {
            path: 'confirmation',
            loadComponent: () => import('./components/ordering/checkout/order-confirmation/order-confirmation.component')
              .then(m => m.OrderConfirmationComponent),
            data: { title: 'Order Confirmation' }
          }
        ]
      },

      // Inventory Management routes
      {
        path: 'inventory',
        loadComponent: () => import('./components/inventory/inventory/inventory')
          .then(m => m.Inventory),
        data: { title: 'Inventory' }
      },
      {
        path: 'receiving',
        loadComponent: () => import('./components/inventory/receiving/receiving')
          .then(m => m.Receiving),
        data: { title: 'Receiving' }
      },
      {
        path: 'waste-tracking',
        loadComponent: () => import('./components/inventory/waste-tracking/waste-tracking')
          .then(m => m.WasteTracking),
        data: { title: 'Waste Tracking' }
      },
      {
        path: 'par-levels',
        loadComponent: () => import('./components/inventory/par-levels/par-levels')
          .then(m => m.ParLevels),
        data: { title: 'Par Levels' }
      },
      {
        path: 'par-levels/alerts',
        loadComponent: () => import('./components/inventory/par-levels/par-alerts/par-alerts.component')
          .then(m => m.ParAlertsComponent),
        data: { title: 'Par Level Alerts' }
      },
      {
        path: 'par-levels/recommendations',
        loadComponent: () => import('./components/inventory/par-levels/reorder-recommendations/reorder-recommendations.component')
          .then(m => m.ReorderRecommendationsComponent),
        data: { title: 'Reorder Recommendations' }
      },

      // Supply Chain & Tracking routes
      {
        path: 'tracking',
        loadComponent: () => import('./components/supply-chain/shipment-tracking/shipment-tracking')
          .then(m => m.ShipmentTracking),
        data: { title: 'Shipment Tracking' }
      },

      // Financial Management routes
      {
        path: 'invoices',
        loadComponent: () => import('./components/financial/invoices/invoices')
          .then(m => m.Invoices),
        data: { title: 'Invoices' }
      },
      {
        path: 'payments',
        loadComponent: () => import('./components/financial/payments/payments')
          .then(m => m.Payments),
        data: { title: 'Payments' }
      },
      {
        path: 'rebates',
        loadComponent: () => import('./components/financial/rebates/rebates')
          .then(m => m.Rebates),
        data: { title: 'Rebates' }
      },
      {
        path: 'financial-reports',
        loadComponent: () => import('./components/financial/financial-reports/financial-reports')
          .then(m => m.FinancialReports),
        data: { title: 'Financial Reports' }
      },

      // Analytics & Insights routes
      {
        path: 'business-intelligence',
        loadComponent: () => import('./components/analytics/business-intelligence/business-intelligence')
          .then(m => m.BusinessIntelligence),
        data: { title: 'Business Intelligence' }
      },
      {
        path: 'cost-analysis',
        loadComponent: () => import('./components/analytics/cost-analysis/cost-analysis')
          .then(m => m.CostAnalysis),
        data: { title: 'Cost Analysis' }
      },
      {
        path: 'performance',
        loadComponent: () => import('./components/analytics/performance/performance')
          .then(m => m.Performance),
        data: { title: 'Performance' }
      },
      {
        path: 'forecasting',
        loadComponent: () => import('./components/analytics/forecasting/forecasting')
          .then(m => m.Forecasting),
        data: { title: 'Forecasting' }
      },

      // Price Comparison & Optimization route
      {
        path: 'price-comparison',
        loadComponent: () => import('./components/price-comparison/price-comparison.component')
          .then(m => m.PriceComparisonComponent),
        data: { title: 'Price Comparison' }
      },

      // AI Assistant routes
      {
        path: 'ai-assistant',
        loadComponent: () => import('./components/ai-assistant/ai-insights-panel/ai-insights-panel.component')
          .then(m => m.AiInsightsPanelComponent),
        data: { title: 'AI Assistant' }
      },
      {
        path: 'ai-insights',
        loadComponent: () => import('./components/ai-assistant/ai-insights-panel/ai-insights-panel.component')
          .then(m => m.AiInsightsPanelComponent),
        data: { title: 'AI Insights' }
      },
      {
        path: 'menu-analyzer',
        loadComponent: () => import('./components/ai-assistant/menu-analyzer/menu-analyzer.component')
          .then(m => m.MenuAnalyzerComponent),
        data: { title: 'Menu Analyzer' }
      },

      // Voice Order route
      {
        path: 'voice-order',
        loadComponent: () => import('./components/voice-order/voice-order.component')
          .then(m => m.VoiceOrderComponent),
        data: { title: 'Voice Order' }
      },

      // Notifications route
      {
        path: 'notifications',
        loadComponent: () => import('./components/notifications/notifications-list/notifications-list')
          .then(m => m.NotificationsList),
        data: { title: 'Notifications' }
      },

      // Settings routes
      {
        path: 'profile',
        loadComponent: () => import('./components/settings/profile/profile')
          .then(m => m.Profile),
        data: { title: 'Profile' }
      },
      {
        path: 'business-settings',
        loadComponent: () => import('./components/settings/business-settings/business-settings')
          .then(m => m.BusinessSettings),
        data: { title: 'Business Settings' }
      },
      {
        path: 'notification-settings',
        loadComponent: () => import('./components/settings/notification-settings/notification-settings')
          .then(m => m.NotificationSettings),
        data: { title: 'Notification Settings' }
      },

      // Scenario 1 routes - AI-Powered Menu Intelligence (Redesigned)
      {
        path: 'scenario1',
        loadComponent: () => import('./components/scenarios/scenario1/scenario1.component')
          .then(m => m.Scenario1Component),
        data: { title: 'Menu Intelligence' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario1/screens/onboarding/onboarding.component')
              .then(m => m.Scenario1OnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'mission-control',
            loadComponent: () => import('./components/scenarios/scenario1/screens/mission-control/mission-control.component')
              .then(m => m.MissionControlComponent),
            data: { title: 'Mission Control' }
          },
          {
            path: 'agent-theater',
            loadComponent: () => import('./components/scenarios/scenario1/screens/agent-theater/agent-theater.component')
              .then(m => m.AgentTheaterComponent),
            data: { title: 'Agent Theater' }
          },
          {
            path: 'intelligence-dashboard',
            loadComponent: () => import('./components/scenarios/scenario1/screens/intelligence-dashboard/intelligence-dashboard.component')
              .then(m => m.IntelligenceDashboardComponent),
            data: { title: 'Intelligence Dashboard' }
          },
          {
            path: 'observability',
            loadComponent: () => import('./components/scenarios/scenario1/screens/observability/observability.component')
              .then(m => m.ObservabilityComponent),
            data: { title: 'Observability' }
          }
        ]
      },

      // Scenario 2 routes - AI Agentic Multi-Agent Supply Chain Optimization
      {
        path: 'scenario2',
        loadComponent: () => import('./components/scenarios/scenario2/scenario2.component')
          .then(m => m.Scenario2Component),
        data: { title: 'Supply Chain Optimization' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario2/onboarding/onboarding.component')
              .then(m => m.Scenario2OnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'welcome',
            loadComponent: () => import('./components/scenarios/scenario2/welcome/welcome.component')
              .then(m => m.Scenario2WelcomeComponent),
            data: { title: 'Welcome' }
          },
          {
            path: 'orchestration',
            loadComponent: () => import('./components/scenarios/scenario2/orchestration/orchestration.component')
              .then(m => m.OrchestrationComponent),
            data: { title: 'Agent Orchestration' }
          },
          {
            path: 'results',
            loadComponent: () => import('./components/scenarios/scenario2/results/results.component')
              .then(m => m.ResultsComponent),
            data: { title: 'Results' }
          }
        ]
      },

      // Scenario 3 routes - AI Agentic Demo (Predictive Demand Planning)
      {
        path: 'scenario3',
        loadComponent: () => import('./components/scenarios/scenario3/scenario3.component')
          .then(m => m.Scenario3Component),
        data: { title: 'Demand Planning' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario3/components/onboarding/onboarding.component')
              .then(m => m.Scenario3OnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'config',
            loadComponent: () => import('./components/scenarios/scenario3/components/mission-config/mission-config.component')
              .then(m => m.MissionConfigComponent),
            data: { title: 'Mission Configuration' }
          },
          {
            path: 'agents',
            loadComponent: () => import('./components/scenarios/scenario3/components/agent-workspace/agent-workspace.component')
              .then(m => m.AgentWorkspaceComponent),
            data: { title: 'Agent Workspace' }
          },
          {
            path: 'results',
            loadComponent: () => import('./components/scenarios/scenario3/components/results-dashboard/results-dashboard.component')
              .then(m => m.ResultsDashboardComponent),
            data: { title: 'Results Dashboard' }
          }
        ]
      },

      // Scenario 1-R routes (duplicate of Scenario 1)
      {
        path: 'scenario1-r',
        loadComponent: () => import('./components/scenarios/scenario1-r/scenario1.component')
          .then(m => m.Scenario1RComponent),
        data: { title: 'Menu Intelligence (R)' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario1-r/screens/onboarding/onboarding.component')
              .then(m => m.Scenario1ROnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'mission-control',
            loadComponent: () => import('./components/scenarios/scenario1-r/screens/mission-control/mission-control.component')
              .then(m => m.MissionControlR1Component),
            data: { title: 'Mission Control' }
          },
          {
            path: 'agent-theater',
            loadComponent: () => import('./components/scenarios/scenario1-r/screens/agent-theater/agent-theater.component')
              .then(m => m.AgentTheaterR1Component),
            data: { title: 'Agent Theater' }
          },
          {
            path: 'intelligence-dashboard',
            loadComponent: () => import('./components/scenarios/scenario1-r/screens/intelligence-dashboard/intelligence-dashboard.component')
              .then(m => m.IntelligenceDashboardR1Component),
            data: { title: 'Intelligence Dashboard' }
          },
          {
            path: 'observability',
            loadComponent: () => import('./components/scenarios/scenario1-r/screens/observability/observability.component')
              .then(m => m.ObservabilityR1Component),
            data: { title: 'Observability' }
          }
        ]
      },

      // Scenario 2-R routes (duplicate of Scenario 2)
      {
        path: 'scenario2-r',
        loadComponent: () => import('./components/scenarios/scenario2-r/scenario2.component')
          .then(m => m.Scenario2RComponent),
        data: { title: 'Supply Chain Optimization (R)' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario2-r/onboarding/onboarding.component')
              .then(m => m.Scenario2ROnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'welcome',
            loadComponent: () => import('./components/scenarios/scenario2-r/welcome/welcome.component')
              .then(m => m.Scenario2RWelcomeComponent),
            data: { title: 'Welcome' }
          },
          {
            path: 'orchestration',
            loadComponent: () => import('./components/scenarios/scenario2-r/orchestration/orchestration.component')
              .then(m => m.OrchestrationR2Component),
            data: { title: 'Agent Orchestration' }
          },
          {
            path: 'results',
            loadComponent: () => import('./components/scenarios/scenario2-r/results/results.component')
              .then(m => m.ResultsR2Component),
            data: { title: 'Results' }
          }
        ]
      },

      // Scenario 3-R routes (duplicate of Scenario 3)
      {
        path: 'scenario3-r',
        loadComponent: () => import('./components/scenarios/scenario3-r/scenario3.component')
          .then(m => m.Scenario3RComponent),
        data: { title: 'Demand Planning (R)' },
        children: [
          { path: '', redirectTo: 'onboarding', pathMatch: 'full' },
          {
            path: 'onboarding',
            loadComponent: () => import('./components/scenarios/scenario3-r/components/onboarding/onboarding.component')
              .then(m => m.Scenario3ROnboardingComponent),
            data: { title: 'Onboarding' }
          },
          {
            path: 'config',
            loadComponent: () => import('./components/scenarios/scenario3-r/components/mission-config/mission-config.component')
              .then(m => m.MissionConfigR3Component),
            data: { title: 'Mission Configuration' }
          },
          {
            path: 'agents',
            loadComponent: () => import('./components/scenarios/scenario3-r/components/agent-workspace/agent-workspace.component')
              .then(m => m.AgentWorkspaceR3Component),
            data: { title: 'Agent Workspace' }
          },
          {
            path: 'results',
            loadComponent: () => import('./components/scenarios/scenario3-r/components/results-dashboard/results-dashboard.component')
              .then(m => m.ResultsDashboardR3Component),
            data: { title: 'Results Dashboard' }
          }
        ]
      }
    ]
  },

  // Fallback routes
  { path: '**', redirectTo: '/dashboard' }
];
